import React, { useCallback, useEffect, useState } from "react";
import { socket } from "../../services/socketIO";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Avatar,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  CircularProgress,
} from "@mui/material";

import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

import axios from "axios";
import { useSelector } from "react-redux";
import AdminMonthSelect, {
  ADMIN_MONTH_OPTIONS,
  normalizeVoteRows,
} from "../../component/admin/AdminMonthSelect";
import {
  currentAdminMonthValue,
  voteCountLabel,
} from "../../utils/adminMonthFilter";

export default function Leaderboard() {
  const token = useSelector((state) => state.auth.token);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [month, setMonth] = useState(currentAdminMonthValue());
  const [search, setSearch] = useState("");
  const votesLabel = voteCountLabel(month, ADMIN_MONTH_OPTIONS);

  const fetchLeaderboard = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/leaderboard/users`,
        {
          params: { month, search },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setUsers(normalizeVoteRows(response.data.data));
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error(error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [token, month, search]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  useEffect(() => {
    socket.emit("joinLeaderboard");

    const handleLeaderboardUpdate = () => {
      fetchLeaderboard();
    };

    socket.on("leaderboardUpdated", handleLeaderboardUpdate);

    return () => {
      socket.off("leaderboardUpdated", handleLeaderboardUpdate);
    };
  }, [fetchLeaderboard]);

  return (
    <React.Fragment>
      <Box className="page_title">
        <Typography variant="h1" component="h1">
          Fanzone Leaderboard
        </Typography>

        <Grid container spacing={2} mb={3}>
          {users.slice(0, 3).map((user, index) => (
            <Grid item xs={12} md={4} key={user.id}>
              <Card
                sx={{
                  borderRadius: "14px",
                  border: "1px solid #edf0f2",
                  boxShadow: "0 2px 8px rgba(15,23,42,0.04)",
                  textAlign: "center",
                  position: "relative",
                }}
              >
                <CardContent>
                  <EmojiEventsIcon
                    sx={{
                      color:
                        index === 0
                          ? "#f59e0b"
                          : index === 1
                          ? "#94a3b8"
                          : "#b45309",
                      fontSize: 36,
                      mb: 1,
                    }}
                  />

                  <Avatar
                    src={user.profile_img}
                    sx={{
                      width: 70,
                      height: 70,
                      margin: "0 auto",
                      mb: 2,
                    }}
                  />

                  <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
                    {user.firstName} {user.lastName}
                  </Typography>

                  <Typography
                    sx={{ fontSize: "13px", color: "#64748b", mb: 2 }}
                  >
                    {user.email}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "24px",
                      fontWeight: 700,
                      color: "#ef4444",
                    }}
                  >
                    {user.totalVotes}
                  </Typography>

                  <Typography sx={{ fontSize: "13px", color: "#64748b" }}>
                    {votesLabel}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card
          sx={{
            borderRadius: "12px",
            border: "1px solid #edf0f2",
            boxShadow: "0 2px 8px rgba(15,23,42,0.04)",
          }}
        >
          <CardContent>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 3,
                flexWrap: "wrap",
              }}
            >
              <TextField
                placeholder="Search user..."
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  minWidth: 240,
                  "& .MuiOutlinedInput-root": {
                    height: 38,
                    fontSize: "13px",
                    background: "#fff",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#dbe2ea",
                  },
                }}
              />

              <AdminMonthSelect
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              />
            </Box>

            {loading ? (
              <Box textAlign="center" py={5}>
                <CircularProgress size={28} sx={{ color: "#ef4444" }} />
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: "12px",
                  border: "1px solid #edf0f2",
                  boxShadow: "none",
                }}
              >
                <Table>
                  <TableHead
                    sx={{
                      background: "#f8fafc",
                      "& th": {
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#475569",
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell>Rank</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>{votesLabel}</TableCell>
                      <TableCell>Videos</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {users.length > 0 ? (
                      users.map((user, index) => (
                        <TableRow key={user.id} hover>
                          <TableCell>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: index === 0 ? "#f59e0b" : "#0f172a",
                              }}
                            >
                              #{index + 1}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              <Avatar
                                src={user.profile_img}
                                sx={{ width: 38, height: 38 }}
                              />
                              <Typography
                                sx={{ fontSize: "13px", fontWeight: 600 }}
                              >
                                {user.firstName} {user.lastName}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell>{user.email}</TableCell>

                          <TableCell>
                            <Typography
                              sx={{ fontWeight: 700, color: "#ef4444" }}
                            >
                              {user.totalVotes}
                            </Typography>
                          </TableCell>

                          <TableCell>{user.totalVideos}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No leaderboard data found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    </React.Fragment>
  );
}
